import React, { useState, useEffect, useCallback } from 'react';
import { adminGet, adminPost, adminPatch } from '../api';
import ImageInput from './ImageInput';

const ITEM_TYPES = [
  { value: 'none',          label: 'None (category/subcategory)' },
  { value: 'external',      label: 'External link' },
  { value: 'video',         label: 'Video (YouTube / Vimeo / direct MP4)' },
  { value: 'movies',        label: 'Movies' },
  { value: 'archive-video', label: 'Archive video' },
  { value: 'archive-audio', label: 'Archive audio' },
];

const SHOW_ONLY_OPTIONS = ['MC', 'otterbein'];

const DEFAULTS = {
  id: '',
  legacy_id: '',
  name: '',
  level: 0,
  parent_id: '',
  sort_order: 0,
  type: 'none',
  image_path: '',
  link: '',
  link_config: '',
  show_only_with: [],
};

/**
 * MenuItemForm — create or edit a menu item at any level.
 *
 * Props:
 *   item       {object|null}  — existing item to edit, or null for new
 *   parentId   {number|null}  — pre-set parent for "add child" flows
 *   level      {number}       — pre-set level (0/1/2)
 *   allItems   {object[]}     — full flat list for parent pickers
 *   onSave     {(item) => void}
 *   onCancel   {() => void}
 */
export default function MenuItemForm({
  item = null,
  parentId = null,
  level: defaultLevel = 0,
  allItems = [],
  onSave,
  onCancel,
}) {
  const isEdit = Boolean(item);

  const [form, setForm] = useState(() => ({
    ...DEFAULTS,
    level: item?.level ?? defaultLevel,
    parent_id: item?.parent_id ?? parentId ?? '',
    ...(item || {}),
    link_config: item?.link_config ? JSON.stringify(item.link_config, null, 2) : '',
    show_only_with: item?.show_only_with ?? [],
  }));

  const [saving, setSaving]   = useState(false);
  const [errors, setErrors]   = useState([]);
  const [idLoading, setIdLoading] = useState(false);

  // Fetch suggested next id when adding a new child
  useEffect(() => {
    if (isEdit || !form.parent_id) return;
    setIdLoading(true);
    adminGet(`/api/admin/menu-items/next-id?parentId=${form.parent_id}`)
      .then(res => setForm(f => ({ ...f, id: res.id, legacy_id: res.legacy_id })))
      .catch(() => { /* ignore */ })
      .finally(() => setIdLoading(false));
  }, [form.parent_id, isEdit]);

  const set = useCallback((key, value) => {
    setForm(f => ({ ...f, [key]: value }));
  }, []);

  const toggleShowOnly = (val) => {
    setForm(f => {
      const current = f.show_only_with || [];
      return {
        ...f,
        show_only_with: current.includes(val)
          ? current.filter(x => x !== val)
          : [...current, val],
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors([]);

    // Parse link_config JSON if provided
    let link_config = null;
    if (form.link_config?.trim()) {
      try {
        link_config = JSON.parse(form.link_config);
      } catch {
        setErrors(['link_config is not valid JSON']);
        return;
      }
    }

    const payload = {
      name:        form.name.trim(),
      level:       Number(form.level),
      sort_order:  Number(form.sort_order),
      type:        form.type,
      image_path:  form.image_path || null,
      link:        form.link?.trim() || null,
      link_config: link_config,
      show_only_with: form.show_only_with?.length ? form.show_only_with : null,
      parent_id:   form.parent_id ? Number(form.parent_id) : null,
      ...(isEdit ? {} : {
        id:        Number(form.id),
        legacy_id: form.legacy_id ? Number(form.legacy_id) : null,
      }),
    };

    setSaving(true);
    try {
      const result = isEdit
        ? await adminPatch(`/api/admin/menu-items/${item.id}`, payload)
        : await adminPost('/api/admin/menu-items', payload);
      onSave(result);
    } catch (err) {
      setErrors([err.message]);
    } finally {
      setSaving(false);
    }
  };

  // Parent options: items whose level = form.level - 1
  const parentOptions = allItems.filter(i => i.level === Number(form.level) - 1);

  return (
    <form className="admin-form" onSubmit={handleSubmit}>
      <h3 style={{ margin: '0 0 20px', fontSize: 16, color: '#1a1a2e' }}>
        {isEdit ? `Edit: ${item.name}` : `Add ${['Category', 'Subcategory', 'Leaf item'][form.level]}`}
      </h3>

      {errors.length > 0 && (
        <div className="admin-banner admin-banner-error">
          {errors.map((e, i) => <div key={i}>{e}</div>)}
        </div>
      )}

      {/* ID fields — only for new items */}
      {!isEdit && (
        <div style={{ display: 'flex', gap: 12 }}>
          <div className="admin-form-row" style={{ flex: 1 }}>
            <label>ID {idLoading && <span style={{ fontWeight: 400 }}>(loading…)</span>}</label>
            <input type="number" value={form.id} onChange={e => set('id', e.target.value)} required min="1" />
            <div className="admin-form-hint">Auto-suggested from parent ID convention; override if needed</div>
          </div>
          <div className="admin-form-row" style={{ flex: 1 }}>
            <label>Legacy ID</label>
            <input type="number" value={form.legacy_id} onChange={e => set('legacy_id', e.target.value)} />
          </div>
        </div>
      )}

      {/* Level */}
      {!isEdit && (
        <div className="admin-form-row">
          <label>Level</label>
          <select value={form.level} onChange={e => set('level', Number(e.target.value))}>
            <option value={0}>0 — Top-level category</option>
            <option value={1}>1 — Subcategory</option>
            <option value={2}>2 — Leaf item</option>
          </select>
        </div>
      )}

      {/* Parent */}
      {Number(form.level) > 0 && (
        <div className="admin-form-row">
          <label>Parent</label>
          <select
            value={form.parent_id}
            onChange={e => set('parent_id', e.target.value)}
            required
          >
            <option value="">— select parent —</option>
            {parentOptions.map(p => (
              <option key={p.id} value={p.id}>{p.name} (id {p.id})</option>
            ))}
          </select>
        </div>
      )}

      {/* Name */}
      <div className="admin-form-row">
        <label>Name</label>
        <input type="text" value={form.name} onChange={e => set('name', e.target.value)} required />
      </div>

      {/* Sort order */}
      <div className="admin-form-row">
        <label>Sort order</label>
        <input type="number" value={form.sort_order} onChange={e => set('sort_order', e.target.value)} />
      </div>

      {/* Type */}
      <div className="admin-form-row">
        <label>Type</label>
        <select value={form.type} onChange={e => set('type', e.target.value)}>
          {ITEM_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
      </div>

      {/* Image */}
      <div className="admin-form-row">
        <ImageInput
          value={form.image_path}
          onChange={v => set('image_path', v)}
          label="Thumbnail image"
        />
      </div>

      {/* Link fields — only for types that use a URL */}
      {['external', 'archive-video', 'archive-audio', 'video'].includes(form.type) && (
        <div className="admin-form-row">
          <label>Link</label>
          <input type="text" value={form.link} onChange={e => set('link', e.target.value)} />
          {form.type === 'video' ? (
            <div className="admin-form-hint">
              Paste a YouTube URL (youtube.com/watch, youtu.be, Shorts), Vimeo URL, or a direct .mp4 link.
            </div>
          ) : (
            <div className="admin-form-hint">URL for this item. If different per facility, leave blank and use Link Config below.</div>
          )}
        </div>
      )}

      {['external', 'archive-video', 'archive-audio', 'video'].includes(form.type) && (
        <div className="admin-form-row">
          <label>Link Config (per-facility JSON)</label>
          <textarea
            rows={5}
            value={form.link_config}
            onChange={e => set('link_config', e.target.value)}
            style={{ fontFamily: 'monospace', fontSize: 12 }}
            placeholder={'{\n  "default": "https://...",\n  "MC": "https://mc-specific..."\n}'}
          />
          <div className="admin-form-hint">
            Optional. Must be a JSON object with a "default" key.
            Per-facility keys (e.g. "MC", "otterbein") override the default.
          </div>
        </div>
      )}

      {/* show_only_with */}
      <div className="admin-form-row">
        <label>Show only with (optional)</label>
        <div style={{ display: 'flex', gap: 16, paddingTop: 4 }}>
          {SHOW_ONLY_OPTIONS.map(opt => (
            <label key={opt} className="admin-checkbox-row">
              <input
                type="checkbox"
                checked={(form.show_only_with || []).includes(opt)}
                onChange={() => toggleShowOnly(opt)}
              />
              {opt}
            </label>
          ))}
        </div>
        <div className="admin-form-hint">Leave empty to show this item for all facility types.</div>
      </div>

      <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
        <button type="submit" className="btn btn-primary" disabled={saving}>
          {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Create item'}
        </button>
        <button type="button" className="btn btn-secondary" onClick={onCancel}>Cancel</button>
      </div>
    </form>
  );
}
